import type { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import axios from "axios";
import qs from "qs";
import { checkAuthentication } from "../auth/identity";
import RefreshTokenService from "../services/RefreshTokenService";
import { updateCurrentAppVersion } from "../services/appVersion";
import type { FuncArgs } from "../types";
import { refreshCookieExists } from "../utilities/cookies";
import type { ApiError, ApiResponse } from "./types";

export interface ExtendedAxiosError<T = unknown, D = unknown> extends AxiosError<T, D> {
  config?: ExtendedAxiosConfig<D>;
}

export interface ExtendedAxiosConfig<D = unknown> extends InternalAxiosRequestConfig<D> {
  skipAuthRefresh?: boolean;
}

export const setUpAxios = (
  nonAuthUserAction: () => void,
  errorPipes?: ((error: ExtendedAxiosError) => Promise<never> | undefined)[]
) => {
  axios.defaults.withCredentials = true;

  axios.defaults.paramsSerializer = (params) => qs.stringify(params, { arrayFormat: "repeat" });

  axios.interceptors.response.use(
    (res) => {
      updateCurrentAppVersion(res.headers["x-entrilia-web-app-version"]);
      return res;
    },
    async (error) => {
      const axiosError = error as ExtendedAxiosError;
      if (!axiosError.isAxiosError) {
        return Promise.reject(axiosError instanceof Error ? axiosError : new Error(getErrorMessage(axiosError)));
      }

      if (errorPipes !== undefined) {
        for (let index = 0; index < errorPipes.length; index++) {
          const pipe = errorPipes[index];
          const result = pipe && pipe(axiosError);
          if (result !== undefined) return result;
        }
      }

      if (axiosError.response?.status === 401 && !axiosError.config?.skipAuthRefresh) {
        if (refreshCookieExists() && (await RefreshTokenService.do())) {
          return axios.request({ ...axiosError.config, skipAuthRefresh: true } as AxiosRequestConfig);
        }

        nonAuthUserAction();

        return Promise.reject(axiosError);
      }

      return Promise.reject(axiosError instanceof Error ? axiosError : new Error(getErrorMessage(axiosError)));
    }
  );

  const nonAuthRequestUrlPatterns = [
    /sso\/auth/i,
    /anonymous\/admin/i,
    /emailverification\/emailverifications\/.+\/complete/i,
    /investor-portal\/(request-code|access-check|blob-probes)/i,
    /troubleshooting\/ping/i,
  ];

  axios.interceptors.request.use((request) => {
    if (nonAuthRequestUrlPatterns.some((regex) => regex.test(request.url ?? ""))) {
      return request;
    }

    return checkAuthentication().then((authenticated) => {
      if (authenticated) {
        return request;
      }

      nonAuthUserAction();
      throw new axios.Cancel("Request canceled: unauthenticated user");
    });
  });
};

export const getErrorMessage = (error: unknown) => {
  if (!error) {
    return "Unknown error";
  }

  if (typeof error === "string") {
    return error;
  }

  if (axios.isAxiosError(error)) {
    const responseData = error.response && (error.response.data as ApiResponse<null>);
    return responseData?.error?.message || error.message || "Unknown error";
  }

  if (error instanceof Error) {
    return error.message || "Unknown error";
  }

  if (typeof error === "object" && "message" in error) {
    return error["message"]?.toString() || "Unknown error";
  }

  return JSON.stringify(error) || "Unknown error";
};

export const getApiError = (error: unknown): ApiError => {
  if (!error) {
    return { message: "Unknown error occurred during API call" };
  }

  if (axios.isCancel(error)) {
    return { isCanceledRequest: true, message: error.message || "Request canceled" };
  }

  const errorMesage = getErrorMessage(error);
  if (!axios.isAxiosError(error)) {
    return { message: `Error occurred during API call: ${errorMesage}` };
  }

  const resp = error.response?.data as ApiResponse<undefined> | undefined;
  if (!resp) {
    return { message: `Request failed: ${errorMesage}`, code: error.response?.status ?? 0 };
  }

  const apiResponse = resp;
  return apiResponse.error ?? { message: `Request failed: ${errorMesage}`, code: error.response?.status ?? 0 };
};

export const getStatusCodeFromError = (error: unknown): number | undefined => {
  if (!error || !axios.isAxiosError(error)) {
    return undefined;
  }

  return error.response?.status;
};

export type HandledApiResponse<TData> = [TData, undefined] | [undefined, ApiError];

export const withErrorHandling =
  <TData, Args extends FuncArgs>(apiCall: (...args: Args) => Promise<ApiResponse<TData>>) =>
  async (...args: Args): Promise<HandledApiResponse<TData>> => {
    try {
      const resp = await apiCall(...args);
      if (resp.success) {
        return [resp.data, undefined];
      } else {
        const apiError = resp.error ?? { message: "Unknown API error" };
        return [undefined, apiError];
      }
    } catch (error) {
      const apiError = getApiError(error);
      return [undefined, apiError];
    }
  };

export const createApiResponse = <TData>(data: TData): ApiResponse<TData> => ({
  success: true,
  data,
});

export const createErrorApiResponse = <TData>(error: ApiError | undefined): ApiResponse<TData> => ({
  success: false,
  error,
});
